<template>
  <v-skeleton-loader
    :loading="skeleton"
    type="input"
    class="b-autocomplete-skeleton"
  >
    <v-autocomplete
      ref="autocomplete"
      v-model="model"
      v-validate="validateRules"
      v-bind="$attrs"
      :items="list"
      :loading="isLoading"
      :search-input="search"
      :clearable="clearable && !required && !multiple"
      :item-text="itemText"
      :item-value="itemValue"
      :name="name"
      hide-details="auto"
      :error="$errors.has(name)"
      :error-messages="errorIcon ? null : $errors.first(name)"
      :data-vv-as="dataVvAs || label ? `«${dataVvAs || label}»` : ' '"
      :return-object="returnObject"
      :hide-selected="false"
      :filter="filterFn"
      :item-disabled="itemDisabled"
      :multiple="multiple"
      :disabled="disabled"
      :small-chips="multiple ? value && value.length > 0 : false"
      outlined
      dense
      :label="label"
      :hide-no-data="!meta || isLoading"
      :menu-props="menuProps"
      class="b-autocomplete"
      v-on="listeners"
      @focus="focus"
      @blur="blur"
      @update:search-input="onSearchInput"
      @input="onInput"
      @change="onChange"
      @click:clear="onClear"
    >
      <template v-if="required && label" #label>
        <span>{{ label }}*</span>
      </template>

      <template v-if="multiple" #selection="{ attrs, item }">
        <v-chip
          v-bind="attrs"
          :close="!disabled"
          small
          class="ma-1"
          @click.prevent.stop
          @click:close="onRemove(item)"
        >
          {{ item[itemText] || item }}
        </v-chip>
      </template>

      <template #item="{ item, attrs }">
        <v-checkbox
          v-if="multiple"
          :input-value="attrs.inputValue"
          readonly
          dense
          hide-details
          class="pa-0 ma-0"
        />

        <v-list-item-content>
          <v-list-item-title
            class="body-2 font-weight-regular"
            style="white-space: initial;"
            v-text="item[itemText] || item"
          />
        </v-list-item-content>
      </template>

      <template
        v-if="canGetDataTotal && !isLoading && !items.length"
        #append-item
      >
        <div
          v-observe-visibility="observeVisibility"
          :class="{ 'mx-4 my-2': (res = list.length > 1 && canGetData) }"
        >
          <v-btn
            v-if="res"
            :loading="isLoading"
            depressed
            small
            block
            @click="onGetNextPage"
            v-text="'Загрузить еще'"
          />
        </div>
      </template>

      <template v-for="(_, key) in $slots" #[key]>
        <slot :name="key" />
      </template>

      <template v-for="(_, key) in $scopedSlots" #[key]="props">
        <slot v-bind="props" :name="key" />
      </template>

      <template #append>
        <v-icon
          :class="{ 'BAutocomplete__menu-icon--opened': isMenuOpened }"
          class="BAutocomplete__menu-icon"
        >
          mdi-menu-down
        </v-icon>

        <slot name="info" />

        <BInfo
          v-if="errorIcon && $errors.has(name)"
          color="error"
          size="20px"
          icon="mdi-alert-circle-outline"
          icon-class="b-autocomplete__error-icon"
          :text="$errors.first(name)"
        />
      </template>
    </v-autocomplete>
  </v-skeleton-loader>
</template>

<script>
import { CancelToken, isCancel } from 'axios'
import { Subject, timer } from 'rxjs'
import {
  map,
  distinctUntilChanged,
  tap,
  filter,
  switchMap,
  debounce,
} from 'rxjs/operators'
import uniqueId from 'lodash/uniqueId'
import { ObserveVisibility } from 'vue-observe-visibility'
import { getErrorMessages } from '@/helpers/getErrorMessages'

import BInfo from '@/components/common/BInfo.vue'

const DEFAULT_PER_PAGE = 50
const DEBAUNCE_TIME = 500

export default {
  name: 'BAutocomplete',
  inject: ['$validator'],
  directives: {
    ObserveVisibility,
  },
  components: {
    BInfo,
  },
  inheritAttrs: false,
  props: {
    validate: [String, Object],
    value: null,
    multiple: Boolean,
    skeleton: Boolean,
    dataVvAs: String,
    errorIcon: Boolean,
    name: {
      type: String,
      default: () => uniqueId('b-autocomplete-'),
    },
    label: {
      type: String,
      // required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: [String, Function],
      default: 'name',
    },
    itemValue: {
      type: [String, Function],
      default: 'id',
    },
    returnObject: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    clearable: {
      type: Boolean,
      default: true,
    },
    request: {
      type: Function,
      required: true,
    },
    exclude: {
      type: Array,
    },
    /**
     * Если true, то isActive будет установлен в false после выбора элемента.
     * isActive отвечает за поиск данных по фразе.
     */
    deactivateAfterInput: {
      type: Boolean,
      default: true,
    },
    /**
     * Очищает поле поиска значения после выбора элемента.
     */
    clearSearchAfterChange: Boolean,
    /**
     * Автоматический выбор подходящего значения при закрытии мени
     */
    autoselectOnBlur: Boolean,
  },
  data(vm) {
    return {
      listeners: this.$listeners || Object.freeze({}),
      isLoading: false,
      list: vm.items,
      isActive: false,
      meta: null,
      model: null,
      menuRef: null,
      menuProps: {
        maxWidth: '480px',
        contentClass: uniqueId('b-autocomplete__content-class-'),
      },
      search: '',
    }
  },
  computed: {
    validateRules() {
      let rules = {}
      if (typeof this.validate === 'string') {
        rules[this.validate] = true
      } else {
        rules = { ...this.validate }
      }

      if (!rules.included && typeof rules.included !== 'boolean' && !this.canGetData) {
        rules.included = !this.returnObject && this.itemValue
          ? this.list.map((item) => item[this.itemValue])
          : this.list
      }

      return rules
    },
    isMenuOpened() {
      return this.menuRef && this.menuRef.isActive
    },
    required() {
      const { validate } = this

      if (typeof validate === 'string') {
        return validate.includes('required')
      }

      return this.validate?.required
    },
    canGetData() {
      return this.list.length !== this.meta?.count
    },
    canGetDataTotal() {
      const { meta } = this
      // TODO: total_count
      const result = this.list.length !== meta?.count
      return result || !!meta?.search
    },
    filterFn() {
      return this.canGetDataTotal ? () => true : void 0
    },
    itemDisabled() {
      const { exclude, itemValue } = this
      if (!exclude?.length) return void 0

      const getId = typeof itemValue === 'function' ? itemValue : (data) => data[itemValue]

      const excludeIds = exclude.map(getId)
      return (data) => excludeIds.includes(getId(data))
    },
    observeVisibility() {
      return this.isActive && this.canGetDataTotal
        ? {
          callback: this.onVisibilityChange,
          throttle: 300,
          throttleOptions: {
            leading: 'visible',
          },
          intersection: {
            root: this.getRefList(),
            rootMargin: '40px',
            threshold: 1.0,
          },
        }
        : false
    },
  },
  watch: {
    items() {
      this.list = this.items
    },
    value: {
      immediate: true,
      handler(value) {
        if (typeof value !== 'undefined' && value !== null) {
          if (this.list.length) this.checkValueByListAndUpdateModel(this.list, value)
          else this.model = value

          // Валидируем сразу, если включен validateOnMount
          if (this.validateOnMount) {
            this.$nextTick(() => {
              this.$validator.validate(this.name)
            })
          }
        }
      },
    },
    list(optsList) {
      this.checkValueByListAndUpdateModel(optsList, this.value)

      // Валидируем сразу, если включен validateOnMount и есть значение
      if (this.validateOnMount && typeof this.value !== 'undefined' && this.value !== null) {
        this.$nextTick(() => {
          this.$validator.validate(this.name)
        })
      }
    },
  },
  async mounted() {
    this.setSubscription()

    if (this.skeleton) {
      await new Promise((resolve) => {
        const handler = () => {
          // eslint-disable-next-line no-use-before-define
          unwatch()
          resolve()
        }

        const unwatch = this.$watch('skeleton', handler)
        this.$once('hook:beforeDestroy', handler)
      })
    }

    if (typeof this.value !== 'undefined' && this.value !== null) {
      if (!this.returnObject) this.onSearchInput(this.value, void 0, true)
      else {
        this.setOnMouseup()
        if (!this.list.length) {
          this.list = [this.value]
        }
      }
    }

    const onTabCapture = (e) => {
      if (e.key !== 'Tab') return
      const input = this.$refs.autocomplete?.$refs?.input
      if (!input) return
      // Find the next focusable element and move focus manually instead of letting
      // the browser's Tab handling fire (which goes through Vuetify's onTabDown and
      // corrupts the display). Manual .focus() makes blur fire cleanly on this input.
      const selector = 'input:not([disabled]), button:not([disabled]), select:not([disabled]),'
        + ' textarea:not([disabled]), a[href], [tabindex]'
      const focusable = Array.from(document.querySelectorAll(selector))
        .filter((el) => el.tabIndex !== -1 && el.offsetParent !== null)
      const currentIdx = focusable.indexOf(input)
      if (currentIdx === -1) return
      const nextEl = focusable[e.shiftKey ? currentIdx - 1 : currentIdx + 1]
      if (!nextEl) return
      e.preventDefault()
      e.stopImmediatePropagation()
      nextEl.focus()
    }
    this.$el.addEventListener('keydown', onTabCapture, true)
    this.$once('hook:beforeDestroy', () => {
      this.$el.removeEventListener('keydown', onTabCapture, true)
    })
  },
  beforeDestroy() {
    // eslint-disable-next-line no-unused-expressions
    this.cancelSource?.cancel(this.name)
  },
  methods: {
    async focus() {
      this.isActive = true

      if (!this.list.length) {
        this.isLoading = true
        const { data, meta } = await this.onGetData()
        if (data) {
          this.meta = meta
          this.list = data
        }
        this.isLoading = false
      }

      if (!this.menuRef && this.$refs.autocomplete) {
        this.menuRef = this.$refs.autocomplete.$refs.menu
      }
    },
    blur() {
      if (this.autoselectOnBlur) this.updateValueVySearch()
      this.isActive = false
    },
    reset() {
      this.meta = null
      this.setOnMouseup()
    },
    checkValueByListAndUpdateModel(list, value) {
      if (list.length && typeof value !== 'undefined' && value !== null) {
        const { itemValue, returnObject } = this
        const getVal = typeof itemValue === 'function'
          ? itemValue
          : (o) => o?.[itemValue] || o
        const valArr = list.map(getVal)
        const getModelVal = (v) => (returnObject ? list[valArr.indexOf(v)] : v)

        if (!this.multiple) {
          const v = getVal(value)
          // eslint-disable-next-line no-restricted-syntax
          for (const listValue of valArr) {
            if (
              listValue === v.toString()
              || listValue === v
              || listValue === value.toString()
              || listValue === value
            ) {
              this.model = getModelVal(listValue)
            }
          }
        } else {
          let checkedValue = value
          if (!(checkedValue instanceof Array)) checkedValue = [checkedValue]
          checkedValue.forEach((item, i) => {
            const v = getVal(item)
            // eslint-disable-next-line no-restricted-syntax
            for (const listValue of valArr) {
              if (
                listValue === v.toString()
                || listValue === v
                || listValue === item.toString()
                || listValue === item
              ) {
                checkedValue[i] = getModelVal(listValue)
                return
              }
            }

            delete checkedValue[i]
          })
          checkedValue = checkedValue.filter((v) => typeof v !== 'undefined')
          this.model = checkedValue.length ? checkedValue : null
        }
      }
    },
    updateValueVySearch() {
      const { list, search, multiple } = this
      if (
        list.length
        && typeof search !== 'undefined'
        && search !== null
        && !multiple
      ) {
        const { itemText, itemValue, returnObject } = this
        const getTitle = typeof itemText === 'function' ? itemText : (o) => o?.[itemText] || o
        const getVal = typeof itemValue === 'function'
          ? itemValue
          : (o) => o?.[itemValue] || o
        const getModelVal = (v) => (returnObject ? v : getVal(v))
        const strictItem = list.filter(
          (item) => String(getTitle(item)) === search,
        )
        const searchItem = list.filter((item) => new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'imu').test(
          String(getTitle(item)),
        ))
        const item = (strictItem.length === 1 && strictItem[0])
          || (searchItem.length === 1 && searchItem[0])
          || null

        if (typeof item !== 'undefined' && item !== null) {
          this.$emit('input', getVal(item))
          this.model = getModelVal(item)
        }
      }

      this.search = ''
    },
    getRefList() {
      const selector = `.${this.menuProps.contentClass}`
      return document.querySelector(selector)
    },
    async onMouseup(force = true) {
      this.isLoading = true

      const params = { search: void 0, per_page: DEFAULT_PER_PAGE }
      const response = await this.onGetData(params)

      if (!response) {
        this.isLoading = false
        return
      }

      const { meta } = response

      if (meta.count <= 2 * DEFAULT_PER_PAGE) {
        this.onSearchInput(void 0, meta.count, force)
      } else {
        this.onSearchInput('', void 0, force)
      }

      // TODO: в onSearchInput есть свой isLoading = false
      // this.isLoading = false
    },
    onRemove(data) {
      const { itemValue } = this
      const getVal = typeof itemValue === 'function' ? itemValue : (o) => o?.[itemValue] || o
      const id = getVal(data)
      const list = this.value.filter((_) => getVal(_) !== id)
      this.$emit('input', list)
    },
    onClear() {
      if (this.canGetDataTotal) {
        this.isLoading = true
        this.list = []
        this.onMouseup(true)
      }
    },
    // eslint-disable-next-line camelcase
    onSearchInput(rawSearch, per_page, force) {
      this.$errors.remove(this.name)

      const search = rawSearch?.toString()

      if (this.isLoading || force) {
        this.search = search
        this.subject$.next({ search, per_page, force })
        return
      }

      // Vuetify emits null on blur to signal a search reset — ignore it so we don't
      // overwrite the lazySearch that Vuetify's updateSelf() just set to the item text.
      if (rawSearch == null) return

      this.search = search

      const { itemText, model } = this
      const getTitle = typeof itemText === 'function'
        ? itemText
        : (o) => model?.[itemText] || o
      if (search && search === getTitle(this.value)) {
        // Прерываем поиск, если строка запроса совпадает с наименованием v-model.
        return
      }

      if (!this.canGetDataTotal) {
        this.$nextTick(() => {
          // Нужен для сброса search когда нет v-model на компоненте
          if (
            (typeof this.value === 'undefined' || this.value === null)
            && this.model
          ) this.model = null
        })
        return
      }

      this.subject$.next({ search, per_page })
    },
    // eslint-disable-next-line camelcase
    async onGetData({ per_page, ...args } = { per_page: DEFAULT_PER_PAGE }) {
      // eslint-disable-next-line no-unused-expressions
      this.cancelSource?.cancel(this.name)

      this.cancelSource = CancelToken.source()
      const cancelToken = this.cancelSource.token
      const payload = { ...args, per_page, force: void 0 }

      try {
        const { meta, data } = await this.request(payload, { cancelToken })
        return { data, meta: { ...payload, ...meta } }
      } catch (e) {
        if (isCancel(e)) return void 0
        const msg = getErrorMessages(e)
        const error = { field: this.name, msg }
        this.$errors.add(error)
        return void 0
      }
    },
    async onGetNextPage() {
      this.isLoading = true

      const params = {
        ...this.meta,
        page: this.meta.page + 1,
      }

      const response = await this.onGetData(params)

      this.isLoading = false

      if (!response) return

      const { meta, data } = response
      this.meta = meta
      this.list = this.list.concat(data)

      const refList = this.getRefList()
      // TODO: хак что бы список подгрузился
      if (refList) refList.scrollTop -= 1
    },
    setOnMouseup() {
      const { autocomplete } = this.$refs

      if (!autocomplete) return

      const mouseupHandler = () => {
        if (
          !this.meta
          || ((typeof this.value === 'undefined' || this.value === null)
            && this.meta.search)
        ) {
          this.onMouseup()
        }
      }

      autocomplete.$on('mouseup', mouseupHandler)

      const unwatch = this.$watch('canGetDataTotal', (value) => {
        if (value) return
        autocomplete.$off('mouseup', mouseupHandler)
        unwatch()
      })

      this.$once('hook:beforeDestroy', () => {
        unwatch()
      })
    },
    onVisibilityChange(isVisible) {
      if (!isVisible) return
      if (!this.canGetData) return
      if (this.isLoading) return
      if (this.meta) this.onGetNextPage()
    },
    setSubscription() {
      const subject$ = new Subject().pipe(
        debounce((_) => timer(_?.force ? 1 : DEBAUNCE_TIME)),
        map((_) => ({ ..._, search: _.search?.trim?.() || void 0 })),
        distinctUntilChanged((a, b) => a?.search === b?.search && !b.force),
        tap(() => {
          this.isLoading = true
          this.list = []
          this.meta = null
        }),
        switchMap(this.onGetData),
        tap(() => {
          this.isLoading = false
        }),
        filter(Boolean),
        tap(({ data, meta }) => {
          this.list = data
          this.meta = meta
        }),
      )

      this.subject$ = subject$

      const subscription = subject$.subscribe()

      this.$once('hook:beforeDestroy', () => {
        subscription.unsubscribe()
      })
    },
    onInput() {
      this.$errors.remove(this.name)
      if (this.deactivateAfterInput) this.isActive = false
    },
    onChange() {
      if (this.clearSearchAfterChange) {
        this.$nextTick(() => {
          this.model = null
          this.onSearchInput(null, null, true)
          this.focus()
        })
      }
    },
  },
}
</script>

<style lang="scss">
.b-autocomplete {
  &.v-text-field.v-text-field--enclosed .v-text-field__details {
    padding: 0 2px;
    margin-bottom: 0;
    font-size: 10px;
  }

  .v-chip--disabled {
    opacity: 1;
  }

  &__error-icon {
    @include animation(shake, 4px);
  }
}

.BAutocomplete__menu-icon {
  transition: transform 0.3s ease;
  transform: rotate(0deg);

  &--opened {
    transform: rotate(180deg);
  }
}
</style>
